/**
 * Competitor data for /alternatives/* and /vs/* SEO pages.
 *
 * Source notes:
 * - travaux.com: blocks WebFetch (403). Data based on public knowledge + brand presence.
 *   Verify quarterly (last verified 2026-05).
 * - habitatpresto.com: WebFetch 2026-05-07 confirmed: 5k pros, 30k reviews, 300k projects,
 *   founded 2005, 5 devis in 48h.
 *
 * Honest stance: we don't fabricate competitor weaknesses. We only state what's verifiable
 * from their public pages. AfficheServices comparisons highlight WHERE we go further, not
 * where competitors fail.
 */

export type Competitor = {
  slug: string
  name: string
  url: string
  founded: number
  positioning: string
  proCount: string
  reviewCount?: string
  responseTime: string
  pricingClient: string
  verificationLevels: string[]
  rgeFilter: 'native' | 'limited' | 'unknown'
  mediation: 'native' | 'limited' | 'unknown'
  whoItsFor: string
  whoItsNotFor: string
  strengths: string[]
  honestLimitations: string[]
  switchingReasons: string[]
}

export const COMPETITORS: Record<string, Competitor> = {
  'travaux-com': {
    slug: 'travaux-com',
    name: 'Travaux.com',
    url: 'https://www.travaux.com',
    founded: 2002,
    positioning: "Marketplace française historique de mise en relation entre particuliers et artisans, avec présence multi-canal (web + télévision).",
    proCount: 'Réseau étendu (chiffres non publics)',
    responseTime: 'Variable selon catégorie',
    pricingClient: 'Gratuit pour les particuliers',
    verificationLevels: ['SIRET via SIRENE', 'Inscription au registre du commerce'],
    rgeFilter: 'limited',
    mediation: 'unknown',
    whoItsFor: "Particuliers cherchant un artisan via une plateforme installée depuis 20+ ans, avec une notoriété forte (campagnes TV).",
    whoItsNotFor: "Particuliers qui veulent un filtre RGE/Qualibat granulaire ou une médiation explicite en cas de litige.",
    strengths: [
      'Marque établie depuis 2002 — 20+ ans d\'historique',
      'Notoriété grand public via campagnes télévisées',
      'Couverture nationale large',
      'Référencement organique fort sur les requêtes génériques',
    ],
    honestLimitations: [
      "Filtre RGE / Qualibat / Qualifelec moins granulaire qu'AfficheServices (selon l'observation des pages catégorie)",
      "Vérification d'identité KYC des artisans non documentée publiquement",
      "Processus de médiation en cas de litige non explicite sur le site",
    ],
    switchingReasons: [
      'Besoin de filtrer par certification RGE pour bénéficier des aides MaPrimeRénov\'',
      'Recherche d\'une vérification d\'identité explicite (KYC) en plus du SIRET',
      'Souhait d\'une médiation gratuite incluse en cas de désaccord',
    ],
  },
  '123devis': {
    slug: '123devis',
    name: '123Devis',
    url: 'https://www.123devis.com',
    founded: 2007,
    positioning: "Plateforme française de comparaison de devis travaux opérant depuis 2007, avec un focus sur la mise en relation gratuite particuliers / artisans.",
    proCount: 'Réseau étendu (chiffres non publics)',
    responseTime: 'Variable',
    pricingClient: 'Demandes de devis gratuites',
    verificationLevels: ['Vérification administrative de base'],
    rgeFilter: 'limited',
    mediation: 'unknown',
    whoItsFor: "Particuliers cherchant une plateforme installée depuis 15+ ans avec demande de devis simple.",
    whoItsNotFor: "Particuliers exigeant un filtre RGE granulaire ou une vérification d'identité formelle des artisans.",
    strengths: [
      "Plateforme historique (depuis 2007)",
      "Présence SEO établie sur le segment 'comparer devis'",
      "Demandes gratuites pour les particuliers",
    ],
    honestLimitations: [
      "Volume d'artisans non publié",
      "Détails de vérification (SIRET, RC Pro, identité KYC) non documentés publiquement",
      "Filtre RGE granulaire non visible",
    ],
    switchingReasons: [
      "Recherche d'une plateforme avec vérification d'identité KYC explicite",
      "Besoin d'un filtre RGE pour bénéficier des aides énergétiques",
      "Souhait d'une médiation gratuite formalisée",
    ],
  },
  'allotravaux': {
    slug: 'allotravaux',
    name: 'AlloTravaux',
    url: 'https://www.allotravaux.com',
    founded: 2003,
    positioning: "Plateforme française d'estimation et de mise en relation pour travaux, avec accès à 250+ guides de prix moyens et informations sur les aides financières.",
    proCount: 'Réseau d\'artisans qualifiés (chiffres non publics)',
    responseTime: 'Variable selon catégorie',
    pricingClient: 'Demandes de devis gratuites, sans engagement',
    verificationLevels: ['Vérification administrative de base'],
    rgeFilter: 'limited',
    mediation: 'unknown',
    whoItsFor: "Particuliers en phase de pré-projet qui veulent consulter des guides détaillés (250+ guides) avant de demander un devis.",
    whoItsNotFor: "Particuliers prêts à signer rapidement et exigeant des artisans pré-vérifiés en KYC + RGE explicite.",
    strengths: [
      "20+ ans d'historique (depuis 2003)",
      "Bibliothèque de 250+ guides de prix moyens",
      "Information sur les aides financières disponibles",
    ],
    honestLimitations: [
      "Volume d'artisans non publié sur la page d'accueil",
      "Processus de vérification (SIRET, RC Pro, identité) non détaillé",
      "Filtre RGE granulaire non mis en avant",
    ],
    switchingReasons: [
      "Recherche d'une vérification d'identité formelle (KYC) en plus du SIRET",
      "Besoin d'un filtre RGE / Qualibat / Qualifelec pour les aides MaPrimeRénov'",
      "Souhait d'un volume d'artisans transparent",
    ],
  },
  'manomano-pro': {
    slug: 'manomano-pro',
    name: 'ManoMano Pro',
    url: 'https://www.manomano.fr',
    founded: 2023,
    positioning: "Service de mise en relation artisans lancé par ManoMano (acteur du DIY européen) en 2023-2024, en complément de leur place de marché bricolage.",
    proCount: 'En croissance (lancement 2023-2024)',
    responseTime: 'Variable',
    pricingClient: 'Mise en relation gratuite',
    verificationLevels: ['Vérification SIRET'],
    rgeFilter: 'unknown',
    mediation: 'unknown',
    whoItsFor: "Particuliers déjà clients de ManoMano DIY qui veulent rester dans l'écosystème pour les travaux nécessitant un artisan.",
    whoItsNotFor: "Particuliers cherchant un service de devis travaux mature, avec historique et vérifications éprouvées.",
    strengths: [
      "Adossé à ManoMano (groupe européen du DIY)",
      "Capital marque fort sur le bricolage",
      "Intégration potentielle avec achat de matériel",
    ],
    honestLimitations: [
      "Service récent (lancement 2023-2024) — peu d'historique de devis travaux",
      "Volume d'artisans en construction",
      "Processus de vérification au-delà du SIRET non documenté",
      "Filtre RGE / Qualibat non publiquement mis en avant",
    ],
    switchingReasons: [
      "Besoin d'une plateforme de devis travaux avec maturité opérationnelle (110 000+ artisans)",
      "Recherche d'une vérification d'identité KYC explicite",
      "Filtre RGE granulaire pour bénéficier des aides énergétiques",
    ],
  },
  'habitatpresto': {
    slug: 'habitatpresto',
    name: 'Habitatpresto',
    url: 'https://www.habitatpresto.com',
    founded: 2005,
    positioning: "Plateforme française de demande de devis travaux mettant en avant 5 000 professionnels, 30 000 avis vérifiés et 300 000 projets réalisés depuis 2005.",
    proCount: '5 000 professionnels (chiffre annoncé sur leur page d\'accueil)',
    reviewCount: '30 000 avis clients',
    responseTime: "Jusqu'à 5 devis en 48 heures",
    pricingClient: 'Devis gratuits pour les particuliers',
    verificationLevels: ['Filtres assurances et labels qualité (selon page d\'accueil)'],
    rgeFilter: 'limited',
    mediation: 'unknown',
    whoItsFor: "Particuliers souhaitant comparer jusqu'à 5 devis sur une plateforme avec un volume d'avis significatif (30 000+).",
    whoItsNotFor: "Particuliers qui veulent un filtre RGE explicite, une vérification d'identité formelle ou un volume d'artisans plus large.",
    strengths: [
      '20 ans d\'historique (depuis 2005)',
      '30 000 avis clients accumulés',
      'Jusqu\'à 5 devis par demande (vs 3 chez AfficheServices)',
      "Volume historique de 300 000 projets réalisés",
    ],
    honestLimitations: [
      "5 000 professionnels référencés (volume plus restreint qu'AfficheServices avec 110 000+)",
      "Détails du processus de vérification (SIRET, RC Pro, identité) non documentés publiquement",
      "Filtre RGE / Qualibat granulaire non visible sur les pages catégorie",
    ],
    switchingReasons: [
      'Recherche d\'un volume d\'artisans plus large (110 000+ vs 5 000)',
      'Besoin d\'un filtre RGE granulaire pour aides énergétiques',
      'Vérification d\'identité KYC explicite des artisans',
    ],
  },
}

export type AfficheServicesProfile = {
  name: 'AfficheServices'
  founded: 2024 // adjust if different
  proCount: string
  responseTime: string
  pricingClient: string
  verificationLevels: string[]
  rgeFilter: 'native'
  mediation: 'native'
  differentiators: Array<{ title: string; detail: string }>
}

export const AFFICHESERVICES: AfficheServicesProfile = {
  name: 'AfficheServices',
  founded: 2024,
  proCount: "Plus de 110 000 artisans référencés en France",
  responseTime: "3 devis sous 48 heures",
  pricingClient: "100 % gratuit pour les particuliers, sans inscription, sans engagement",
  verificationLevels: [
    'Vérification SIRET automatique via la base SIRENE de l\'INSEE',
    'Contrôle de l\'assurance responsabilité civile professionnelle',
    'Vérification d\'identité (KYC) pour les comptes Pro et Enterprise',
  ],
  rgeFilter: 'native',
  mediation: 'native',
  differentiators: [
    {
      title: 'Vérification 3 niveaux',
      detail: 'SIRET via SIRENE + contrôle RC Pro + vérification d\'identité KYC. La plupart des plateformes vérifient seulement le SIRET.',
    },
    {
      title: 'Filtre RGE / Qualibat / Qualifelec',
      detail: 'Activable directement sur les pages catégorie. Indispensable pour bénéficier de MaPrimeRénov\', CEE et éco-PTZ.',
    },
    {
      title: 'Médiation gratuite incluse',
      detail: 'En cas de litige avec un artisan, AfficheServices intervient gratuitement comme médiateur. Aucun coût supplémentaire pour le particulier.',
    },
    {
      title: '100 % gratuit pour le client',
      detail: 'Sans inscription, sans engagement, sans frais cachés. Notre modèle économique repose sur les artisans Pro / Enterprise — jamais sur les particuliers.',
    },
    {
      title: '3 devis sous 48 heures',
      detail: 'Réponse standard sous 48h, intervention sous 4h pour les urgences (plomberie, électricité, serrurerie 24/7).',
    },
  ],
}
